import React, { useMemo, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags';
import {
	getAnnotationThreadKeys,
	getVisibleCommentThreads,
	useCommentsPanel,
	ViewValues,
	useCommentsPanelScroll,
} from '@confluence/comments-panel-utils';
import { useActiveCommentsQuery, useCommentsData } from '@confluence/comments-data';
import { fg } from '@confluence/feature-gating';
import { usePageContentId } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { SIDEBAR_COMMENTS_PANEL_LOAD_OPEN_EXPERIENCE } from '@confluence/experience-tracker';

import { Loading } from '../components/Loading';
import { ErrorPanel } from '../components/ErrorPanel';
import { EmptyStateComponent } from '../components/EmptyStateComponent';
import { EmptyOpenViewSVG } from '../assets/EmptyOpenView';
import { CommentsPanelList } from '../components/CommentsPanelList';
import { onlyHasReactionsSummaryErrors } from '../helper/commentHelpers';
import { useCommentsPanelMetrics } from '../hooks/useCommentsPanelMetrics';
import { COMMENTS_PANEL_DELETED_CONTENT_VIEW_METRIC } from '../perf.config';

import {
	DEFAULT_TOP_LEVEL_ACTIONS,
	DEFAULT_TOP_LEVEL_ACTIONS_WITHOUT_DELETE,
} from './topLevelActions';

const i18n = defineMessages({
	emptyCommentsHeaderText: {
		id: 'comments-panel.empty.deleted.content.comments.header.text',
		defaultMessage: 'No comments on deleted content',
		description:
			'Header text to display in Deleted Content View empty state when there are no open comments on the page.',
	},
	emptyCommentsBodyText: {
		id: 'comments-panel.empty.deleted.content.comments.body.text',
		defaultMessage: 'Comments attached to deleted content will be saved here.',
		description:
			'Body text to display in Deleted Content View empty state when there are no Open comments on the page.',
	},
});

type DeletedContentViewProps = {
	showDeleteOption: boolean;
	flags?: FlagsStateContainer;
};

export const DeletedContentView = ({ showDeleteOption, flags }: DeletedContentViewProps) => {
	const { formatMessage } = useIntl();
	const pageMode = useGetPageMode();
	const [contentId] = usePageContentId();
	const [
		{
			initialDataLoadedForViewMap,
			showInlineComments,
			currentView,
			currentlySelectedThreadKey,
			commentType,
		},
		{ setInitialDataLoadedForView },
	] = useCommentsPanel();
	const [
		{ orderedActiveAnnotationIdList, commentsDataMap, removedThreadsMap },
		{ clearRemovedComments },
	] = useCommentsData();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const [allCommentsRendered, setAllCommentsRendered] = useState(false);
	const [commentBatchRendered, setCommentBatchRendered] = useState(false);

	const { loading, error, refetch, data } = useActiveCommentsQuery({
		pageId: contentId || '',
	});

	useCommentsPanelMetrics({
		loading,
		error,
		allCommentsRendered,
		commentBatchRendered,
		totalComments: data?.comments?.nodes?.length,
		contentId,
		pageMode,
		experienceName: SIDEBAR_COMMENTS_PANEL_LOAD_OPEN_EXPERIENCE,
		metric: COMMENTS_PANEL_DELETED_CONTENT_VIEW_METRIC,
	});

	useEffect(() => {
		// When this view unmounts, we want to clear out the "resolved"/"deleted" comments
		return () => {
			clearRemovedComments();
		};
	}, [clearRemovedComments]);

	useEffect(() => {
		if (!loading) {
			setInitialDataLoadedForView({ viewToSetLoaded: ViewValues.DELETED_CONTENT });

			// handle focused comment
			if (currentlySelectedThreadKey) {
				scrollCommentsPanelToThread({
					annotationId: currentlySelectedThreadKey,
					commentType,
					isCommentThreadResolved: false,
					scrollOnLoad: true,
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- ignore change in currentlySelectedThreadKey since we only want to scroll the first time for focused comments
	}, [loading, setInitialDataLoadedForView]);

	const isInitialCommentDataLoaded = initialDataLoadedForViewMap[ViewValues.DELETED_CONTENT];

	const commentThreads = useMemo(() => {
		if (
			(!isInitialCommentDataLoaded && loading) ||
			(orderedActiveAnnotationIdList.length === 0 && Object.keys(removedThreadsMap).length === 0)
		) {
			return [];
		}

		return getVisibleCommentThreads({
			currentView,
			commentsDataMap,
			includeGeneralComments: false,
			includeInlineComments: showInlineComments,
			orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
		});
	}, [
		commentsDataMap,
		currentView,
		isInitialCommentDataLoaded,
		loading,
		orderedActiveAnnotationIdList,
		removedThreadsMap,
		showInlineComments,
	]);

	if (!isInitialCommentDataLoaded) {
		if (loading) {
			return <Loading />;
		}

		if (
			error &&
			(!onlyHasReactionsSummaryErrors(error) || !fg('confluence_frontend_handle_reactions_errors'))
		) {
			return (
				<ErrorPanel
					error={error}
					onRetryClick={async () => {
						await refetch();
					}}
				/>
			);
		}
	}

	if (commentThreads.length === 0) {
		return (
			<EmptyStateComponent
				SVG={EmptyOpenViewSVG}
				emptyCommentsHeaderText={formatMessage(i18n.emptyCommentsHeaderText)}
				emptyCommentsBodyText={formatMessage(i18n.emptyCommentsBodyText)}
			/>
		);
	}

	return (
		<CommentsPanelList
			commentThreads={commentThreads}
			supportedTopLevelActions={
				showDeleteOption ? DEFAULT_TOP_LEVEL_ACTIONS : DEFAULT_TOP_LEVEL_ACTIONS_WITHOUT_DELETE
			}
			flags={flags}
			onAllCommentsRendered={() => setAllCommentsRendered(true)}
			onCommentBatchRendered={() => setCommentBatchRendered(true)}
		/>
	);
};
